import React, { useEffect, useRef, useState } from 'react';
import DateComparisonView from '../view/DateComparisonView';
import dateLogicFieldsToDateRule from '../../../utils/dateLogicFieldsToDateRule';

const DateComparisonEngine = ({
  updateDateRule = () => {},
  intervalUnit,
  intervalCount,
  op,
  value,
  variant
}) => {
  const isFirstRender = useRef(true);

  const [anytimeDropdownValue, setAnytimeDropdownValue] = useState('anytime');
  const [comparisonDropdownValue, setComparisonDropdownValue] = useState(null);
  const [firstIntervalCount, setFirstIntervalCount] = useState(null);
  const [firstIntervalUnit, setFirstIntervalUnit] = useState(null);
  const [secondIntervalCount, setSecondIntervalCount] = useState(null);
  const [secondIntervalUnit, setSecondIntervalUnit] = useState(null);
  const [firstDate, setFirstDate] = useState(null);
  const [secondDate, setSecondDate] = useState(null);

  useEffect(() => {
    if (isFirstRender.current) {
      updateDisplayFromData();
      return;
    }
    if (
      anytimeDropdownValue === 'anytime' &&
      (comparisonDropdownValue === 'future' ||
        comparisonDropdownValue === 'past') &&
      (firstIntervalCount ||
        firstIntervalUnit ||
        secondIntervalCount ||
        secondIntervalUnit ||
        firstDate ||
        secondDate)
    ) {
      setFirstIntervalCount(null);
      setFirstIntervalUnit(null);
      setSecondIntervalCount(null);
      setSecondIntervalUnit(null);
      setFirstDate(null);
      setSecondDate(null);
      return;
    }
    if (anytimeDropdownValue === 'between') {
      if (!firstIntervalCount) {
        setFirstIntervalCount(1);
      }
      if (!firstIntervalUnit) {
        setFirstIntervalUnit('day');
      }
      if (!secondIntervalCount) {
        setSecondIntervalCount(1);
      }
      if (!secondIntervalUnit) {
        setSecondIntervalUnit('day');
      }
    }
    if (anytimeDropdownValue === 'more' || anytimeDropdownValue === 'less') {
      if (!firstIntervalCount) {
        setFirstIntervalCount(1);
      }
      if (!firstIntervalUnit) {
        setFirstIntervalUnit('day');
      }
      if (secondIntervalCount || secondIntervalUnit) {
        setSecondIntervalCount(null);
        setSecondIntervalUnit(null);
      }
    }
    if (
      comparisonDropdownValue === 'blank' ||
      comparisonDropdownValue === 'not_blank'
    ) {
      setAnytimeDropdownValue('anytime');
      setFirstIntervalCount(null);
      setFirstIntervalUnit(null);
      setSecondIntervalCount(null);
      setSecondIntervalUnit(null);
      setFirstDate(null);
      setSecondDate(null);
    }
    updateDataForBackend();
  }, [
    anytimeDropdownValue,
    comparisonDropdownValue,
    firstIntervalCount,
    firstIntervalUnit,
    secondIntervalCount,
    secondIntervalUnit,
    firstDate,
    secondDate
  ]);

  const updateDisplayFromData = () => {
    let firstArrayObject, secondArrayObject;
    if (op && value) {
      if (value === 'now') {
        if (intervalUnit && intervalCount) {
          if (op === '<') {
            setComparisonDropdownValue('past');
            setAnytimeDropdownValue('more');
            setFirstIntervalCount(intervalCount);
            setFirstIntervalUnit(intervalUnit);
          } else if (op === '>') {
            setComparisonDropdownValue('future');
            setAnytimeDropdownValue('more');
            setFirstIntervalCount(intervalCount);
            setFirstIntervalUnit(intervalUnit);
          }
        } else {
          setComparisonDropdownValue(op === '>' ? 'future' : 'past');
          setAnytimeDropdownValue('anytime');
        }
      } else if (Array.isArray(value) && value.length === 2) {
        firstArrayObject = value[0];
        secondArrayObject = value[1];
        if (
          firstArrayObject.type === 'date' &&
          secondArrayObject.type === 'date'
        ) {
          setComparisonDropdownValue('between');
          setFirstDate(value[0]?.value);
          setSecondDate(value[1]?.value);
        } else if (firstArrayObject.type === 'date') {
          setComparisonDropdownValue('past');
          setAnytimeDropdownValue('less');
          setFirstIntervalCount(secondArrayObject.intervalCount);
          setFirstIntervalUnit(secondArrayObject.intervalUnit);
        } else if (secondArrayObject.type === 'date') {
          setComparisonDropdownValue('future');
          setAnytimeDropdownValue('less');
          setFirstIntervalCount(firstArrayObject.intervalCount);
          setFirstIntervalUnit(firstArrayObject.intervalUnit);
        } else if (
          firstArrayObject.type === 'interval' &&
          secondArrayObject.type === 'interval'
        ) {
          setComparisonDropdownValue(
            firstArrayObject.op === 'plus' ? 'future' : 'past'
          );
          setAnytimeDropdownValue('between');
          setFirstIntervalCount(firstArrayObject.intervalCount);
          setFirstIntervalUnit(firstArrayObject.intervalUnit);
          setSecondIntervalCount(secondArrayObject.intervalCount);
          setSecondIntervalUnit(secondArrayObject.intervalUnit);
        }
      } else {
        setComparisonDropdownValue(op);
        setFirstDate(value);
      }
    } else if (op === '<>') {
      setComparisonDropdownValue('not_blank');
      setAnytimeDropdownValue('anytime');
      setFirstIntervalCount(null);
      setFirstIntervalUnit(null);
      setSecondIntervalCount(null);
      setSecondIntervalUnit(null);
    } else if (op === '=' && value === '') {
      setComparisonDropdownValue('blank');
      setAnytimeDropdownValue('anytime');
      setFirstIntervalCount(null);
      setFirstIntervalUnit(null);
      setSecondIntervalCount(null);
      setSecondIntervalUnit(null);
    } else {
      setComparisonDropdownValue('=');
      setAnytimeDropdownValue('anytime');
      setFirstIntervalCount(null);
      setFirstIntervalUnit(null);
      setSecondIntervalCount(null);
      setSecondIntervalUnit(null);
    }
    setTimeout(() => {
      isFirstRender.current = false;
    }, 1000);
  };

  const updateDataForBackend = () => {
    updateDateRule(
      dateLogicFieldsToDateRule({
        anytimeDropdownValue,
        comparisonDropdownValue,
        firstIntervalCount,
        firstIntervalUnit,
        secondIntervalCount,
        secondIntervalUnit,
        firstDate,
        secondDate
      })
    );
  };

  const onComparisonDropdownChanged = newValue => {
    setComparisonDropdownValue(newValue);
  };

  const onAnytimeDropdownChanged = newValue => {
    setAnytimeDropdownValue(newValue);
  };

  const onFirstIntervalCountChanged = e => {
    setFirstIntervalCount(e.target.value);
  };

  const onFirstIntervalUnitChanged = newValue => {
    setFirstIntervalUnit(newValue);
  };

  const onSecondIntervalCountChanged = e => {
    setSecondIntervalCount(e.target.value);
  };

  const onSecondIntervalUnitChanged = newValue => {
    setSecondIntervalUnit(newValue);
  };

  const onFirstDatePickerChanged = date => {
    setFirstDate(date);
  };

  const onSecondDatePickerChanged = date => {
    setSecondDate(date);
  };

  const parseDate = date => {
    let parsedDate = new Date(date);

    if (!(parsedDate instanceof Date && !isNaN(parsedDate))) {
      parsedDate = new Date(parseInt(date));
    }
    return parsedDate;
  };

  const viewProps = {
    anytimeDropdownValue,
    comparisonDropdownValue,
    onAnytimeDropdownChanged,
    onComparisonDropdownChanged,
    onFirstIntervalCountChanged,
    onFirstIntervalUnitChanged,
    onSecondIntervalCountChanged,
    onSecondIntervalUnitChanged,
    onFirstDatePickerChanged,
    onSecondDatePickerChanged,
    parseDate,
    showAnytimeDropdown: ['future', 'past'].includes(comparisonDropdownValue),
    showAnytimeStaticWord: !['future', 'past', 'blank', 'not_blank'].includes(
      comparisonDropdownValue
    ),
    showFirstDate: !['future', 'past', 'blank', 'not_blank'].includes(
      comparisonDropdownValue
    ),
    showSecondDate: comparisonDropdownValue === 'between',
    showFirstInterval:
      ['future', 'past'].includes(comparisonDropdownValue) &&
      anytimeDropdownValue !== 'anytime',
    showSecondInterval:
      ['future', 'past'].includes(comparisonDropdownValue) &&
      anytimeDropdownValue === 'between',
    firstIntervalCount,
    firstIntervalUnit,
    secondIntervalCount,
    secondIntervalUnit,
    firstDate,
    secondDate,
    variant
  };

  return <DateComparisonView {...viewProps} />;
};

export default DateComparisonEngine;
